import React, { useState, useEffect } from 'react';
import {
  Zap,
  Settings,
  Eye,
  EyeOff,
  TestTube,
  Save,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Building2
} from 'lucide-react';
import { toast } from 'react-toastify';
import { optimizedApi } from '@/utils/api/optimized-api';

interface RunoConfig {
  api_url: string;
  auth_key: string;
  process_name: string;
  assigned_to: string;
  company_name: string;
  is_active: boolean;
  auto_sync: boolean;
}

interface RunoConfigResponse {
  uuid: string;
  api_url: string;
  process_name: string;
  assigned_to: string;
  company_name: string;
  is_active: boolean;
  auto_sync: boolean;
  config: Record<string, any>;
  created_at: string;
  updated_at: string;
}

const RunoSettings: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [showAuthKey, setShowAuthKey] = useState(false);

  const [config, setConfig] = useState<RunoConfig>({
    api_url: 'https://api.runo.in/v1/crm/allocation',
    auth_key: '',
    process_name: '',
    assigned_to: '',
    company_name: '',
    is_active: false,
    auto_sync: true
  });

  // Load configuration on mount
  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    setIsLoading(true);
    try {
      const response = await optimizedApi.get<RunoConfigResponse>('/runo/config');
      console.log('🔍 Runo configuration loaded:', response.data);
      if (response.success && response.data) {
        const data = response.data as RunoConfigResponse;
        setConfig({
          api_url: data.api_url || 'https://api.runo.in/v1/crm/allocation',
          auth_key: '', // Don't load auth key for security
          process_name: data.process_name || '',
          assigned_to: data.assigned_to || '',
          company_name: data.company_name || '',
          is_active: data.is_active || false,
          auto_sync: data.auto_sync !== undefined ? data.auto_sync : true
        });
      }
    } catch (error: any) {
      // 404 is expected when no configuration exists yet - don't show error
      const errorMessage = error?.message || error?.toString() || '';
      console.log('🔍 Caught error:', { error, errorMessage, hasNotFound: errorMessage.includes('not found') });

      if (!errorMessage.toLowerCase().includes('not found') && !errorMessage.includes('404')) {
        console.error('Error loading configuration:', error);
        toast.error('Failed to load configuration');
      } else {
        console.log('ℹ️ No Runo configuration found - this is normal for first time setup');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    // Validate required fields
    if (!config.auth_key) {
      toast.error('Auth Key is required');
      return;
    }
    if (!config.process_name) {
      toast.error('Process Name is required');
      return;
    }
    if (!config.assigned_to) {
      toast.error('Assigned To is required');
      return;
    }

    setIsSaving(true);
    try {
      const response = await optimizedApi.post('/runo/config', config);
      if (response.success) {
        toast.success('Configuration saved successfully');
        await loadConfiguration(); // Reload to get updated data
      } else {
        toast.error(response.message || 'Failed to save configuration');
      }
    } catch (error: any) {
      console.error('Error saving configuration:', error);
      toast.error(error?.response?.data?.message || 'Failed to save configuration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestConnection = async () => {
    // Validate required fields
    if (!config.auth_key) {
      toast.error('Auth Key is required for testing');
      return;
    }
    if (!config.process_name) {
      toast.error('Process Name is required for testing');
      return;
    }
    if (!config.assigned_to) {
      toast.error('Assigned To is required for testing');
      return;
    }

    setIsTesting(true);
    try {
      const response = await optimizedApi.post('/runo/test', {
        api_url: config.api_url,
        auth_key: config.auth_key,
        process_name: config.process_name,
        assigned_to: config.assigned_to,
        company_name: config.company_name || 'Test Company'
      });

      if (response.success) {
        toast.success('Connection test successful! Runo API is working correctly.');
      } else {
        toast.error(response.message || 'Connection test failed');
      }
    } catch (error: any) {
      console.error('Connection test failed:', error);
      toast.error(error?.response?.data?.message || 'Connection test failed. Please check your credentials.');
    } finally {
      setIsTesting(false);
    }
  };

  const handleDeleteConfig = async () => {
    if (!confirm('Are you sure you want to delete this configuration? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await optimizedApi.delete('/runo/config');
      if (response.success) {
        toast.success('Configuration deleted successfully');
        setConfig({
          api_url: 'https://api.runo.in/v1/crm/allocation',
          auth_key: '',
          process_name: '',
          assigned_to: '',
          company_name: '',
          is_active: false,
          auto_sync: true
        });
      } else {
        toast.error(response.message || 'Failed to delete configuration');
      }
    } catch (error: any) {
      console.error('Error deleting configuration:', error);
      toast.error(error?.response?.data?.message || 'Failed to delete configuration');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <RefreshCw className="h-6 w-6 text-brandColor animate-spin" />
          <span className="text-gray-600 dark:text-gray-400">Loading configuration...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-8 py-4">
        <div className="flex items-center justify-between">
          {/* Left Content */}
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="p-2 bg-brandColor rounded-lg text-white">
                <Zap className="h-5 w-5" />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                Runo Integration
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Configure CRM allocation and automation with Runo
              </p>
            </div>
          </div>

          {/* Right Content - Status Badge */}
          <div className="flex-shrink-0">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              config.is_active
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
            }`}>
              {config.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="max-w-4xl">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 space-y-6">
              {/* Configuration Section */}
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <Settings className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    API Configuration
                  </h3>
                </div>

                <div className="space-y-4">
                  {/* API URL */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      API URL
                    </label>
                    <input
                      type="url"
                      value={config.api_url}
                      onChange={(e) => setConfig({ ...config, api_url: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                      placeholder="https://api.runo.in/v1/crm/allocation"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Runo API endpoint URL for lead allocation
                    </p>
                  </div>

                  {/* Auth Key */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Auth Key <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={config.auth_key}
                        onChange={(e) => setConfig({ ...config, auth_key: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white pr-10"
                        placeholder="Enter your Runo authentication key"
                        autoComplete="off"
                        spellCheck="false"
                        style={!showAuthKey ? {
                          fontFamily: 'text-security-disc, monospace',
                          WebkitTextSecurity: 'disc',
                          MozTextSecurity: 'disc',
                        } as React.CSSProperties : {}}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowAuthKey(!showAuthKey)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      >
                        {showAuthKey ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Authentication key provided by Runo (e.g., MW04d2k3cGljdjQzZTZiNHI=)
                    </p>
                  </div>

                  {/* Process Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Process Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={config.process_name}
                      onChange={(e) => setConfig({ ...config, process_name: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., Sales Process"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Default process name for lead allocation in Runo
                    </p>
                  </div>

                  {/* Assigned To */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Assigned To <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={config.assigned_to}
                      onChange={(e) => setConfig({ ...config, assigned_to: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., Sales Team"
                      required
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Default assignee for leads in Runo
                    </p>
                  </div>

                  {/* Company Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Company Name
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Building2 className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        value={config.company_name}
                        onChange={(e) => setConfig({ ...config, company_name: e.target.value })}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brandColor dark:bg-gray-700 dark:text-white"
                        placeholder="e.g., Your Company Name"
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Optional company name to include in lead data sent to Runo
                    </p>
                  </div>

                  {/* Settings */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div className="space-y-3">
                      {/* Active Toggle */}
                      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <div>
                          <label htmlFor="active" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Enable Integration
                          </label>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Activate Runo integration to start sending leads
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          id="active"
                          checked={config.is_active}
                          onChange={(e) => setConfig({ ...config, is_active: e.target.checked })}
                          className="h-4 w-4 text-brandColor rounded focus:ring-brandColor"
                        />
                      </div>

                      {/* Auto Sync Toggle */}
                      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <div>
                          <label htmlFor="auto_sync" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            Auto Sync New Leads
                          </label>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Automatically send new leads to Runo when created
                          </p>
                        </div>
                        <input
                          type="checkbox"
                          id="auto_sync"
                          checked={config.auto_sync}
                          onChange={(e) => setConfig({ ...config, auto_sync: e.target.checked })}
                          className="h-4 w-4 text-brandColor rounded focus:ring-brandColor"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Help Section */}
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-1">
                        How to use Runo Integration
                      </h4>
                      <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
                        <li>• Configure your Runo API credentials and default settings above</li>
                        <li>• Test the connection to ensure everything works correctly</li>
                        <li>• Enable the integration to start sending leads to Runo</li>
                        <li>• New leads will be automatically allocated in Runo based on your settings</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <button
                    onClick={handleTestConnection}
                    disabled={isTesting || !config.auth_key || !config.process_name || !config.assigned_to}
                    className="flex items-center space-x-2 px-4 py-2 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isTesting ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <TestTube className="h-4 w-4" />
                    )}
                    <span>{isTesting ? 'Testing...' : 'Test Connection'}</span>
                  </button>

                  <div className="flex items-center space-x-3">
                    {config.process_name && (
                      <button
                        onClick={handleDeleteConfig}
                        className="px-4 py-2 text-red-600 dark:text-red-400 border border-red-300 dark:border-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      >
                        Delete Configuration
                      </button>
                    )}
                    <button
                      onClick={handleSaveConfig}
                      disabled={isSaving || !config.auth_key || !config.process_name || !config.assigned_to}
                      className="flex items-center space-x-2 px-6 py-2 bg-brandColor text-white rounded-lg hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {isSaving ? (
                        <>
                          <RefreshCw className="h-4 w-4 animate-spin" />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4" />
                          <span>Save Configuration</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Success Message */}
              {config.is_active && config.process_name && (
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div className="flex items-center space-x-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                    <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                    <span className="text-sm text-green-800 dark:text-green-200">
                      Runo integration is active and ready to process leads
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RunoSettings;
